import React, {Component} from 'react';
import PropTypes from 'prop-types';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';
import DateUtils from 'date-utils';
import DatePicker from 'date-picker-component';
import TimePicker from 'time-picker-component';
import Tooltip from 'tooltip-component';
import SHGlobals from 'sh-globals';

class DateTimePicker extends Component {
    constructor(props) {
        super(props);

        const {
            selectedDate,
            selectedTime,
            tooltipType,
            tooltipMessage
        } = props;

        this._ID = uuidV4();
        this._IS_NATIVE_DATE_INPUT = SHGlobals.isMobile && DateUtils.supportsDateInput();
        this._DATE_PICKER_CLASS = `DateTimePicker-date-${this._ID}`;
        this._TIME_PICKER_CLASS = `DateTimePicker-time-${this._ID}`;
        this._tooltipHidden = this._tooltipHidden.bind(this);
        this._dateChange = this._dateChange.bind(this);
        this._timeChange = this._timeChange.bind(this);

        this.state = {
            selectedDate: (this._IS_NATIVE_DATE_INPUT) ? DateUtils.convertToMobile(selectedDate) : selectedDate,
            selectedTime,
            tipType: tooltipType,
            tipMessage: tooltipMessage
        };
        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const {
            selectedDate,
            selectedTime,
            tooltipType,
            tooltipMessage
        } = nextProps;
        const {
            selectedDate: stateSelectedDate,
            selectedTime: stateSelectedTime,
            tipType
        } = this.state;
        const nextSelectedDate = (this._IS_NATIVE_DATE_INPUT) ? DateUtils.convertToMobile(selectedDate) : selectedDate;
        const newState = {};

        if (nextSelectedDate !== stateSelectedDate || selectedTime !== stateSelectedTime) {
            newState.selectedDate = nextSelectedDate;
            newState.selectedTime = selectedTime;
        }

        if (tooltipType !== tipType) {
            newState.tipType = tooltipType;
            newState.tipMessage = tooltipMessage;
        }

        this.setState(newState);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            onChange
        } = this.props;
        const {
            selectedDate,
            selectedTime
        } = this.state;

        if ((selectedDate !== prevState.selectedDate || selectedTime !== prevState.selectedTime) && onChange) {
            onChange({
                selectedDate,
                selectedTime
            });
        }
    }

    _tooltipHidden() {
        const {
            onTooltipHidden
        } = this.props;

        if (onTooltipHidden) {
            onTooltipHidden();
        }

        this.setState({
            tipType: 'none',
            tipMessage: null,
            tipTrigger: null
        });
    }

    _dateChange(selectedDate) {
        this.setState({
            selectedDate
        });
    }

    _timeChange(selectedTime) {
        this.setState({
            selectedTime
        });
    }

    render() {
        const {
            className,
            useIcons,
            dateLabel,
            timeLabel,
            timeStep,
            tooltipPosition,
            updateTooltipPositionOnMove,
            calendarClassName,
            disablePast,
            disableBefore,
            disableAfter,
            useTetherShim,
            datePlaceholder,
            timePlaceholder,
            focusDate,
            focusTime
        } = this.props;
        const {
            selectedDate,
            selectedTime,
            tipType,
            tipMessage
        } = this.state;
        const classes = classNames(
            'DateTimePicker',
            className
        );

        return (
            <div className={classes}>
                <DatePicker
                    className={this._DATE_PICKER_CLASS}
                    calendarClassName={calendarClassName}
                    useIcon={useIcons}
                    label={dateLabel}
                    focus={focusDate}
                    selectedDate={selectedDate}
                    onChange={this._dateChange}
                    disablePast={disablePast}
                    disableBefore={disableBefore}
                    disableAfter={disableAfter}
                    useTetherShim={useTetherShim}
                    placeholder={datePlaceholder}
                />
                {tipType === 'date' &&
                    <Tooltip
                        updateOnMove={updateTooltipPositionOnMove}
                        onHidden={this._tooltipHidden}
                        position={tooltipPosition}
                        triggerItem={`.${this._DATE_PICKER_CLASS} .Textfield`}
                    >
                        {tipMessage}
                    </Tooltip>
                }
                <TimePicker
                    className={this._TIME_PICKER_CLASS}
                    useIcon={useIcons}
                    label={timeLabel}
                    focus={focusTime}
                    selectedTime={selectedTime}
                    step={timeStep}
                    onChange={this._timeChange}
                    placeholder={timePlaceholder}
                />
                {tipType === 'time' &&
                    <Tooltip
                        updateOnMove={updateTooltipPositionOnMove}
                        onHidden={this._tooltipHidden}
                        position={tooltipPosition}
                        triggerItem={`.${this._TIME_PICKER_CLASS} .Select`}
                    >
                        {tipMessage}
                    </Tooltip>
                }
            </div>
        );
    }
}

DateTimePicker.propTypes = {
    className: PropTypes.string,
    calendarClassName: PropTypes.string,
    useIcons: PropTypes.bool,
    dateLabel: PropTypes.node,
    timeLabel: PropTypes.node,
    /** A string in the format 'MM/DD/YYYY' representing the date which should be selected. */
    selectedDate: PropTypes.string,
    /** A string in the format of 'hh:mm A' representing the time which should be selected. */
    selectedTime: PropTypes.string,
    focusDate: PropTypes.bool,
    focusTime: PropTypes.bool,
    timeStep: PropTypes.number,
    onChange: PropTypes.func,
    tooltipType: PropTypes.oneOf(['none', 'date', 'time']),
    tooltipMessage: PropTypes.string,
    tooltipPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    updateTooltipPositionOnMove: PropTypes.bool,
    onTooltipHidden: PropTypes.func,
    disablePast: PropTypes.bool,
    disableBefore: PropTypes.string,
    disableAfter: PropTypes.string,
    useTetherShim: PropTypes.bool,
    datePlaceholder: PropTypes.string,
    timePlaceholder: PropTypes.string
};

DateTimePicker.defaultProps = {
    className: '',
    calendarClassName: '',
    useIcons: true,
    dateLabel: 'Date:',
    timeLabel: 'Time:',
    selectedDate: '',
    selectedTime: '',
    timeStep: 30,
    tooltipType: 'none',
    tooltipMessage: null,
    tooltipPosition: 'bottom',
    updateTooltipPositionOnMove: false,
    disablePast: true
};

export default DateTimePicker;



// WEBPACK FOOTER //
// ./frontend/src/js/components/date-time-picker/DateTimePicker.jsx